import React from 'react';

import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { Box, Button, IconButton, InputAdornment, TextField, Typography } from '@mui/material';

import { useNotification } from 'src/hooks/app';
import { useCurrentUserPermissions } from 'src/hooks/user';
import { ResendInviteModalArgs } from 'src/modals/invite';

type ResendInviteModalContentProps = {
  onCloseModal: () => void;
  switchClosability: (closability: boolean) => void;
} & ResendInviteModalArgs;

export const ResendInviteModalContent: React.FC<ResendInviteModalContentProps> = ({
  onCloseModal,
  switchClosability,
  toStaff,
  staffEmail,
  onSubmit,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [inviteLinkUrl, setInviteLinkUrl] = React.useState<string>();

  const { spawnNotification } = useNotification();
  const { hasPlatformRoles } = useCurrentUserPermissions();

  const handleReinvite = React.useCallback(async () => {
    if (hasPlatformRoles) {
      switchClosability(false);
      setLoading(true);
      const data = await onSubmit();
      if (data?.inviteLinkUrl) {
        setInviteLinkUrl(data.inviteLinkUrl);
      }
      setLoading(false);
      switchClosability(true);
    } else {
      onSubmit();
      onCloseModal();
    }
  }, [hasPlatformRoles, onCloseModal, onSubmit, switchClosability]);

  const handleCopyLink = React.useCallback(() => {
    if (!inviteLinkUrl) return;
    navigator.clipboard
      .writeText(inviteLinkUrl)
      .then(() => {
        spawnNotification('Invite link copied to clipboard', 'success');
      })
      .catch(() => spawnNotification('Failed to copy invite link', 'error'));
  }, [inviteLinkUrl, spawnNotification]);

  return (
    <React.Fragment>
      <Typography variant="body1">
        Please confirm that you would like to re-invite this {toStaff} staff ({staffEmail}). This
        will cause the previous invite to become inactive.
      </Typography>

      {inviteLinkUrl && (
        <Box mt={2}>
          <TextField
            label="Invite link"
            value={inviteLinkUrl}
            fullWidth
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton title="Copy Invite link" onClick={() => handleCopyLink()}>
                      <ContentCopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      )}

      <Box display="flex" alignItems="center" justifyContent="center" gap={4} mt={2}>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={onCloseModal}
          loading={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleReinvite}
          loading={loading}
          disabled={loading || !!inviteLinkUrl}
        >
          Yes, re-invite
        </Button>
      </Box>
    </React.Fragment>
  );
};
